import {Component, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';

import {translateCognitoError} from '../../utils/auth-utils';
import {AmplifyAuthenticatorModule} from '@aws-amplify/ui-angular';
import type {AuthenticatorMachineOptions, SocialProvider} from "@aws-amplify/ui";
import {CommonModule} from '@angular/common';
import {Router, RouterLink} from '@angular/router';
import {Hub} from 'aws-amplify/utils';
import {confirmSignIn, confirmSignUp, resendSignUpCode, signIn, signInWithRedirect} from 'aws-amplify/auth';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {EnvironmentConfigService} from '../../core/config/environment-config.service';
import {AuthenticationService} from '../../services/authentication/authentication.service';
import {firstValueFrom} from 'rxjs';

@Component({
  selector: 'app-login',
  imports: [CommonModule, AmplifyAuthenticatorModule, RouterLink],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css',
  // Note: Setting encapsulation to None allows this component's CSS to affect child/library DOM
  // (e.g., elements rendered by Amplify Authenticator). This is optional but useful if you
  // prefer to keep Authenticator-specific styles inside login.component.css instead of styles.css.
  encapsulation: ViewEncapsulation.None
})
export class LoginComponent implements OnInit, OnDestroy {

  loginMechanisms: AuthenticatorMachineOptions['loginMechanisms'] = ['email']; // Can be 'email','phone_number','username'
  socialProviders: SocialProvider[] = [];
  signUpAttributes: AuthenticatorMachineOptions['signUpAttributes'] = [
    "name",
    "family_name",
    "email",
    "phone_number",
    "address",
    "birthdate"
  ]

  isSigningIn = false;
  isFederating = false;
  signInError: string | null = null;

  // Confirm sign-up flow when user tries to sign in but is not confirmed
  needsConfirmation = false;
  emailForConfirm: string = '';
  isConfirming = false;
  isResending = false;
  confirmError: string | null = null;
  confirmSuccess: string | null = null;

  // New password required flow (when user has a temporary password)
  needsNewPassword = false;
  isSettingNewPassword = false;
  newPasswordError: string | null = null;
  newPasswordSuccess: string | null = null;

  // For deferred checkout after login
  private stripeMonthlyPriceId: string = '';
  private stripeAnnualPriceId: string = '';
  private apiBaseUrl: string = '';

  private hubUnsubscribe?: () => void;

  // Helper to detect 'user not confirmed' across different error shapes from Cognito/Amplify
  private isUserNotConfirmedError(e: any): boolean {
    const code = e?.code || e?.name || e?.__type;
    if (code === 'UserNotConfirmedException') return true;
    const msg = (e?.message || (typeof e === 'string' ? e : '') || '').toLowerCase();
    return msg.includes('not confirmed');
  }

  constructor(private router: Router,
              private httpClient: HttpClient,
              private environmentConfigService: EnvironmentConfigService,
              private authService: AuthenticationService) {
    // Initialize env values
    this.stripeMonthlyPriceId = this.environmentConfigService.StripeMonthlyPriceId;
    this.stripeAnnualPriceId = this.environmentConfigService.StripeAnnualPriceId;
    this.apiBaseUrl = this.environmentConfigService.apiUrl;

    // Listen for Amplify Auth events and redirect on successful sign-in
    this.hubUnsubscribe = Hub.listen('auth', ({payload}) => {
      if (payload?.event === 'signedIn') {
        // Check for deferred subscription checkout
        let pendingPlan: { planType?: 'monthly' | 'annual'; ts?: number } | null = null;
        try {
          const raw = localStorage.getItem('pendingSubscription');
          pendingPlan = raw ? JSON.parse(raw) : null;
        } catch (_) {
          pendingPlan = null;
        }

        if (pendingPlan && pendingPlan.planType) {
          this.redirectToCheckout(pendingPlan.planType).catch(err => {
            console.error('Deferred checkout error:', err);
            this.clearPendingSubscription();
            this.router.navigate(['/usuario']);
          });
        } else {
          this.router.navigate(['/usuario']);
        }
      }
    });
  }

  ngOnInit(): void {
    // Listen for Amplify Auth events and redirect on successful sign-in (duplicate listener safeguard)
    this.hubUnsubscribe = Hub.listen('auth', ({payload}) => {
      if (payload?.event === 'signedIn') {
        let pendingPlan: { planType?: 'monthly' | 'annual'; ts?: number } | null = null;
        try {
          const raw = localStorage.getItem('pendingSubscription');
          pendingPlan = raw ? JSON.parse(raw) : null;
        } catch (_) {
          pendingPlan = null;
        }

        if (pendingPlan && pendingPlan.planType) {
          this.redirectToCheckout(pendingPlan.planType).catch(err => {
            console.error('Deferred checkout error:', err);
            this.clearPendingSubscription();
            this.router.navigate(['/usuario']);
          });
        } else {
          this.router.navigate(['/usuario']);
        }
      }
    });
  }

  ngOnDestroy(): void {
    // Clean up the Hub listener to prevent memory leaks
    if (this.hubUnsubscribe) {
      this.hubUnsubscribe();
    }
  }

  async navigateToDashboard() {
    await this.router.navigate(['/usuario']);
  }

  async customSignIn(event: Event) {
    event.preventDefault();
    this.signInError = null;
    this.needsConfirmation = false;
    this.needsNewPassword = false;
    this.confirmError = null;
    this.confirmSuccess = null;
    this.newPasswordError = null;
    this.newPasswordSuccess = null;
    this.isSigningIn = true;

    const form = event.target as HTMLFormElement;
    const fd = new FormData(form);
    const username = (fd.get('username') as string)?.trim();
    const password = fd.get('password') as string;

    try {
      if (!username || !password) {
        this.signInError = 'Informe e-mail e senha.';
        return;
      }
      const result: any = await signIn({username, password});
      // Handle flows that return as a successful response with nextStep (no exception thrown)
      const step = result?.nextStep?.signInStep;
      if (step === 'CONFIRM_SIGN_UP') {
        // User must confirm their account before signing in
        this.needsConfirmation = true;
        this.emailForConfirm = username || '';
        this.signInError = null;
        try {
          await resendSignUpCode({username: this.emailForConfirm});
          this.confirmSuccess = 'Sua conta ainda não foi confirmada. Enviamos um novo código para ' + this.emailForConfirm + '.';
        } catch (_) {
          // ignore resend failures here; user can try manually
        }
        return; // stop normal sign-in flow; wait for confirmation
      } else if (step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        // Temp password users must set a new password
        this.needsNewPassword = true;
        this.emailForConfirm = username || '';
        this.signInError = null;
        return; // show new password form
      }
      // Hub listener in ngOnInit will handle navigation on 'signedIn'
    } catch (e: any) {
      console.error('Error signing in:', e);
      if (this.isUserNotConfirmedError(e)) {
        // Switch to the confirmation flow within the login page
        this.needsConfirmation = true;
        this.emailForConfirm = username || '';
        this.signInError = null;
        // Try to send a new code silently
        try {
          await resendSignUpCode({username: this.emailForConfirm});
          this.confirmSuccess = 'Sua conta ainda não foi confirmada. Enviamos um novo código para ' + this.emailForConfirm + '.';
        } catch (_) {
          // ignore resend failures here; user can try manually
        }
      } else {
        this.signInError = translateCognitoError(e, e?.message || 'Falha ao entrar. Verifique suas credenciais.');
      }
    } finally {
      this.isSigningIn = false;
    }
  }

  // Forward the Forgot Password custom form to Amplify Authenticator
  onForgotSubmit(event: Event, context: any) {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    const fd = new FormData(form);
    context.submitForm(fd);
  }

  // Forward the Confirm Reset Password custom form to Amplify Authenticator
  onConfirmResetSubmit(event: Event, context: any) {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    const fd = new FormData(form);

    // Submit the form to Amplify
    const maybePromise = context.submitForm(fd);

    // After submission, when it is no longer pending and without errors, redirect to success page
    const navigateOnSuccess = () => {
      // Small delay to allow context to update
      const tryCheck = () => {
        try {
          if (!context.isPending) {
            if (!context.error) {
              this.router.navigate(['/senha-alterada-com-sucesso']);
            }
            return; // stop checking regardless of success or error once pending is false
          }
        } catch (_) {
          // if context access fails, do nothing
          return;
        }
        setTimeout(tryCheck, 100);
      };
      setTimeout(tryCheck, 0);
    };

    // If submitForm returns a promise, wait for it to resolve/reject; otherwise, start polling immediately
    if (maybePromise && typeof maybePromise.then === 'function') {
      maybePromise.finally(navigateOnSuccess);
    } else {
      navigateOnSuccess();
    }
  }

  // Forward the Confirm Sign-In (verification code/MFA) form to Amplify Authenticator
  onConfirmSignInSubmit(event: Event, context: any) {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    const fd = new FormData(form);
    context.submitForm(fd);
  }

  // Handle the sign-up confirmation (account not yet confirmed) during login
  async onConfirmSignUpSubmit(event: Event) {
    event.preventDefault();
    this.confirmError = null;
    this.confirmSuccess = null;
    const form = event.target as HTMLFormElement;
    const fd = new FormData(form);
    const code = (fd.get('confirmation_code') as string)?.trim();
    if (!code) {
      this.confirmError = 'Informe o código de confirmação.';
      return;
    }
    this.isConfirming = true;
    try {
      await confirmSignUp({username: this.emailForConfirm, confirmationCode: code});
      // Do not auto sign-in or store the password; ask the user to log in
      this.confirmSuccess = 'Conta confirmada com sucesso! Agora você pode entrar.';


      this.needsConfirmation = false;
    } catch (e: any) {
      this.confirmError = translateCognitoError(e, e?.message || 'Falha ao confirmar. Verifique o código e tente novamente.');
    } finally {
      this.isConfirming = false;
    }
  }

  // Handle the NEW_PASSWORD_REQUIRED flow (set a new password after using a temporary password)
  async onNewPasswordSubmit(event: Event) {
    event.preventDefault();
    this.newPasswordError = null;
    this.newPasswordSuccess = null;
    const form = event.target as HTMLFormElement;
    const fd = new FormData(form);
    const newPassword = (fd.get('new_password') as string) || '';
    const confirm = (fd.get('confirm_new_password') as string) || '';
    if (!newPassword || !confirm) {
      this.newPasswordError = 'Informe e confirme a nova senha.';
      return;
    }
    if (newPassword !== confirm) {
      this.newPasswordError = 'As senhas não coincidem.';
      return;
    }
    this.isSettingNewPassword = true;
    try {
      // For Amplify v6, confirmSignIn handles NEW_PASSWORD_REQUIRED with the new password as challengeResponse
      const output = await confirmSignIn({ challengeResponse: newPassword });
      if (output.isSignedIn) {
        // Hub 'signedIn' will redirect; keep UI responsive
        this.newPasswordSuccess = 'Senha atualizada. Entrando...';
        this.needsNewPassword = false;
      } else if (output.nextStep?.signInStep === 'DONE') {
        this.needsNewPassword = false;
      }
    } catch (e: any) {
      this.newPasswordError = translateCognitoError(e, e?.message || 'Falha ao atualizar a senha. Tente novamente.');
    } finally {
      this.isSettingNewPassword = false;
    }
  }

  async resendConfirmationCode() {
    this.confirmError = null;
    this.isResending = true;
    try {
      await resendSignUpCode({username: this.emailForConfirm});
      this.confirmSuccess = 'Novo código enviado para ' + this.emailForConfirm + '.';
    } catch (e: any) {
      this.confirmError = translateCognitoError(e, e?.message || 'Falha ao reenviar código. Tente novamente.');
    } finally {
      this.isResending = false;
    }
  }

  backToSignIn() {
    this.needsConfirmation = false;
    this.needsNewPassword = false;
    this.confirmError = null;
    this.confirmSuccess = null;
    this.newPasswordError = null;
    this.newPasswordSuccess = null;
  }

  async signInWithGoogle() {
    this.signInError = null;
    this.isFederating = true;
    try {
      await signInWithRedirect({provider: 'Google'});
      // Redirects to Cognito Hosted UI; upon return, Hub 'signedIn' event will fire
    } catch (e: any) {
      this.signInError = translateCognitoError(e, e?.message || 'Falha ao redirecionar para o Google.');
      this.isFederating = false;
    }
  }

  private clearPendingSubscription() {
    try {
      localStorage.removeItem('pendingSubscription');
    } catch (_) {
    }
  }

  private async redirectToCheckout(planType: 'monthly' | 'annual'): Promise<void> {
    const priceId = planType === 'monthly' ? this.stripeMonthlyPriceId : this.stripeAnnualPriceId;

    const checkoutData = {
      priceId: priceId,
      successUrl: `${window.location.origin}/checkout`,
      cancelUrl: `${window.location.origin}/planos`,
    };

    let headers = new HttpHeaders()
      .set('content-type', 'application/json')
      .set('Access-Control-Allow-Origin', '*');

    try {
      const idToken = await this.authService.getIdToken();
      if (idToken) {
        headers = headers.set('Authorization', `Bearer ${idToken}`);
      }
    } catch (error) {
      console.error('Error getting authentication token:', error);
    }

    try {
      const response = await firstValueFrom(
        this.httpClient.post<any>(
          `${this.apiBaseUrl}/create-checkout-session`,
          checkoutData,
          { headers, observe: 'response' as const }
        )
      );

      // If backend created a Stripe session, it should respond with 201
      if (response.status === 201) {
        const url = response.body?.url || response.headers.get('Location') || '';
        if (url) {
          this.clearPendingSubscription();
          window.location.href = url;
          return;
        }
        throw new Error('201 without URL');
      }

      // Otherwise, just navigate to provided path (or url treated as path)
      const path = response.body?.path || response.body?.url;
      if (path) {
        this.clearPendingSubscription();
        await this.router.navigateByUrl(path);
        return;
      }

      throw new Error('Invalid response from server');
    } catch (error) {
      console.error('Error creating checkout session:', error);
      // As a fallback, go to plans page
      this.clearPendingSubscription();
      await this.router.navigate(['/planos']);
    }
  }

}
